/**
 * =============================================================================
 * WlSubscriptionsListCard
 *
 * Card com a listagem de assinaturas dos tenants seguindo o mesmo layout
 * visual do card "Contratações recentes" (WlRecentContractsCard):
 *   - Header com título, subtítulo (contador) e filtros pill à direita
 *   - Tabela com header sticky e scroll vertical dentro do card
 *   - Linhas clicáveis → abre WlTenantDetailsModal (drill-down)
 *   - Paginação server-side via PaginationFooter (pageSizeOptions 15/30/50)
 *
 * Consome a RPC existente `wl_list_tenant_subscriptions` sem alterações.
 *
 * @version 1.2.0
 * RELEASE: 2026-07-22 v1.2.0 - Adiciona pills de filtro "Suspensos" e "Inativos"
 *          (valores distintos de eff_status em tenants.subscription_status:
 *          'suspended' = suspensão manual/billing; 'inactive' = sem assinatura
 *          ativa vinculada). RPC já aceita ambos via p_subscription_status,
 *          nenhuma migration necessária.
 * RELEASE: 2026-07-22 v1.1.0 - Fix filtro "Trial": RPC retorna eff_status='trial'
 *          (não 'trialing'). Pill agora envia 'trial' para casar com o valor real.
 * RELEASE: 2026-07-22 v1.0.0 - Versão inicial (substitui a listagem antiga
 *          da aba Assinaturas em WlFinancial.tsx).
 * =============================================================================
 */
import { useEffect, useMemo, useState } from 'react';
import { CreditCard, Search } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { WlTenantDetailsModal } from '@/components/wl/WlTenantDetailsModal';
import { cn } from '@/lib/utils';

// ----- Tipos -----------------------------------------------------------------
interface SubRow {
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  tenant_status: string;
  plan_id: string | null;
  plan_name: string | null;
  billing_period: string | null;
  subscription_status: string | null;
  price_monthly: number | null;
  price_yearly: number | null;
  current_period_start: string | null;
  current_period_end: string | null;
  trial_ends_at: string | null;
  canceled_at: string | null;
  setup_fee_paid: boolean | null;
  active_addons_count: number;
  total_count: number;
}

// ----- Helpers ---------------------------------------------------------------
const fmtBRL = (v: number | null | undefined) =>
  v == null
    ? '—'
    : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
        Number(v),
      );

const fmtDate = (v: string | null) => (v ? new Date(v).toLocaleDateString('pt-BR') : '—');

/** Rótulo amigável para a recorrência (mesmo padrão de WlRecentContractsCard). */
const recurrenceLabel = (r: string | null) => {
  if (!r) return '—';
  const norm = r.toLowerCase();
  if (norm.startsWith('year') || norm.startsWith('anu')) return 'Anual';
  if (norm.startsWith('month') || norm.startsWith('mens')) return 'Mensal';
  return r;
};

/** Cores por status de assinatura. */
const statusBadgeClass: Record<string, string> = {
  active: 'border-green-500/40 text-green-600 dark:text-green-400',
  trialing: 'border-blue-500/40 text-blue-600 dark:text-blue-400',
  trial: 'border-blue-500/40 text-blue-600 dark:text-blue-400',
  past_due: 'border-yellow-500/40 text-yellow-600 dark:text-yellow-400',
  canceled: 'border-red-500/40 text-red-600 dark:text-red-400',
  incomplete: 'border-muted-foreground/30 text-muted-foreground',
  unpaid: 'border-red-500/40 text-red-600 dark:text-red-400',
  suspended: 'border-amber-500/40 text-amber-600 dark:text-amber-400',
  inactive: 'border-muted-foreground/30 text-muted-foreground',
};

// Filtros locais (pills) — mapeiam para o param p_subscription_status da RPC.
// Valores refletem o eff_status real retornado por wl_list_tenant_subscriptions.
type LocalFilter = null | 'active' | 'trial' | 'suspended' | 'inactive' | 'canceled';

interface Props {
  /** Placeholder para futuras integrações; hoje a lista tem filtro próprio. */
  className?: string;
}

/**
 * Card de listagem de assinaturas — layout espelhado do "Contratações recentes".
 */
export function WlSubscriptionsListCard({ className }: Props) {
  const { toast } = useToast();

  // Paginação e filtros locais
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [statusFilter, setStatusFilter] = useState<LocalFilter>(null);
  const [search, setSearch] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');

  const [rows, setRows] = useState<SubRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Drill-down por linha
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);

  // Carrega dados da RPC (server-side)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.rpc('wl_list_tenant_subscriptions', {
          p_search: committedSearch.trim() || null,
          p_subscription_status: statusFilter,
          p_plan: null,
          p_limit: pageSize,
          p_offset: (page - 1) * pageSize,
        });
        if (error) throw error;
        if (cancelled) return;
        const list = (data ?? []) as SubRow[];
        setRows(list);
        setTotal(list[0]?.total_count ?? 0);
      } catch (err: any) {
        if (!cancelled) {
          toast({
            title: 'Erro ao carregar assinaturas',
            description: err.message,
            variant: 'destructive',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, statusFilter, committedSearch, toast]);

  // Botão pill do filtro local (Todos / Ativos / Trial / Cancelados)
  const PillBtn = useMemo(
    () =>
      function PillBtn({ value, label }: { value: LocalFilter; label: string }) {
        const active = statusFilter === value;
        return (
          <Button
            size="sm"
            variant={active ? 'default' : 'outline'}
            className="h-7 px-2.5 text-xs"
            onClick={() => {
              setStatusFilter(value);
              setPage(1);
            }}
          >
            {label}
          </Button>
        );
      },
    [statusFilter],
  );

  return (
    <Card className={cn('flex flex-col', className)}>
      <CardHeader className="flex flex-col gap-3 pb-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-0.5 min-w-0">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-muted-foreground" />
            Assinaturas
          </CardTitle>
          <span className="text-[11px] text-muted-foreground">
            Assinaturas de planos dos tenants — {total} registro{total === 1 ? '' : 's'}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {/* Busca inline */}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Buscar tenant…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setCommittedSearch(search);
                  setPage(1);
                }
              }}
              onBlur={() => {
                if (search !== committedSearch) {
                  setCommittedSearch(search);
                  setPage(1);
                }
              }}
              className="h-7 pl-7 pr-2 text-xs w-[180px]"
            />
          </div>
          <PillBtn value={null} label="Todos" />
          <PillBtn value="active" label="Ativos" />
          <PillBtn value="trial" label="Trial" />
          <PillBtn value="suspended" label="Suspensos" />
          <PillBtn value="inactive" label="Inativos" />
          <PillBtn value="canceled" label="Cancelados" />
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <div className="max-h-[420px] overflow-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead className="text-xs font-medium">Tenant</TableHead>
                <TableHead className="text-xs font-medium">Plano</TableHead>
                <TableHead className="text-xs font-medium">Recorrência</TableHead>
                <TableHead className="text-xs font-medium text-right">Valor</TableHead>
                <TableHead className="text-xs font-medium">Status</TableHead>
                <TableHead className="text-xs font-medium">Vigência atual</TableHead>
                <TableHead className="text-xs font-medium text-right">Add-ons</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center py-6 text-sm text-muted-foreground"
                  >
                    Carregando…
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center py-6 text-sm text-muted-foreground"
                  >
                    Nenhuma assinatura encontrada
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((s) => {
                  const price =
                    s.billing_period === 'yearly' ? s.price_yearly : s.price_monthly;
                  const badgeCls =
                    statusBadgeClass[s.subscription_status ?? ''] ??
                    'border-muted-foreground/30 text-muted-foreground';
                  return (
                    <TableRow
                      key={s.tenant_id}
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => setSelectedTenantId(s.tenant_id)}
                      title="Abrir detalhes do tenant"
                    >
                      <TableCell className="py-2 text-xs font-medium">
                        <div className="flex flex-col">
                          <span className="truncate">{s.tenant_name}</span>
                          <span className="text-[10px] text-muted-foreground">
                            {s.tenant_slug}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="py-2 text-xs">
                        {s.plan_name ?? s.plan_id ?? '—'}
                      </TableCell>
                      <TableCell className="py-2 text-xs text-muted-foreground">
                        {recurrenceLabel(s.billing_period)}
                      </TableCell>
                      <TableCell className="py-2 text-xs text-right font-medium">
                        {fmtBRL(price)}
                      </TableCell>
                      <TableCell className="py-2">
                        <Badge
                          variant="outline"
                          className={cn('text-[10px] font-medium whitespace-nowrap', badgeCls)}
                        >
                          {s.subscription_status ?? '—'}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-2 text-[11px] whitespace-nowrap">
                        {fmtDate(s.current_period_start)} → {fmtDate(s.current_period_end)}
                      </TableCell>
                      <TableCell className="py-2 text-xs text-right">
                        {s.active_addons_count}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {total > pageSize && (
          <PaginationFooter
            totalItems={total}
            currentPage={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(sz) => {
              setPageSize(sz);
              setPage(1);
            }}
            pageSizeOptions={[15, 30, 50]}
          />
        )}
      </CardContent>

      {/* Drill-down: modal de detalhes do tenant selecionado */}
      <WlTenantDetailsModal
        tenantId={selectedTenantId}
        open={!!selectedTenantId}
        onClose={() => setSelectedTenantId(null)}
      />
    </Card>
  );
}
